import React, { useEffect, useState } from 'react';
import { BookOpen, Headphones, Users, Download, Quote, Settings, ChevronDown, ChevronUp, Video } from 'lucide-react';
import StatsCard from '../components/dashboard/StatsCard';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services';
import { audioService } from '../services';
import { quoteService } from '../services';
import { analyticsService } from '../services';
import videoService from '../services/videoService';
import ebookService from '../services/ebookService';
import { logger } from '../utils/logger';
//write a function to get the total number of ebooks, audio books, active users, and total downloads



const Dashboard = () => {
  const [stats, setStats] = useState<any>({
    totalEbooks: 0,
    totalAudioBooks: 0,
    totalVideos: 0,
    totalQuotes: 0,
    activeUsers: 0,
    totalDownloads: "0",
    latestQuote: null,
    latestEbook: null,
    latestAudio: null,
    latestVideo: null,
  });
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const deleteDraftEBooks = async () => {
      const result = await ebookService.deleteDraftEBooks();
      logger.debug('deleteDraftEBooks result:', result);
    }
    const deleteDraftAudioBooks = async () => {
      const result = await audioService.deleteDraftAudioBooks();
      logger.debug('deleteDraftAudioBooks result:', result);
    }
    const deleteDraftVideos = async () => {
      const result = await videoService.deleteDraftVideos();
      logger.debug('deleteDraftVideos result:', result);
    }
    deleteDraftEBooks();
    deleteDraftAudioBooks();
    deleteDraftVideos();
  }, []);
  useEffect(() => {
    const fetchTotalStats = async () => {
      try {
        setLoading(true);
        const [ebookResult, audioResult, videoResult, quoteResult, userResult, latestContent] = await Promise.all([
          ebookService.getEBooks(),
          audioService.getAudioBooks(),
          videoService.getVideos(),
          quoteService.getQuotes(),
          userService.getUsers(),
          analyticsService.getLatestContent()
        ]);
        
        setStats({
          totalEbooks: ebookResult.data.length,
          totalAudioBooks: audioResult.data.length,
          totalVideos: (videoResult.data || []).length,
          totalQuotes: quoteResult.data.length,
          activeUsers: userResult.data.length,
          totalDownloads: "0",
          latestQuote: latestContent.latestQuote,
          latestEbook: latestContent.latestEbook,
          latestAudio: latestContent.latestAudio,
          latestVideo: latestContent.latestVideo,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchTotalStats();
  }, []);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-heading text-primary-800">Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Welcome back! Here's what's happening with your content.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatsCard 
          title="Total E-books" 
          value={stats.totalEbooks} 
          subtitle="Published e-books"
          icon={BookOpen} 
          color="green" 
        />
        <StatsCard 
          title="Total Audio Books" 
          value={stats.totalAudioBooks} 
          subtitle="Published audio"
          icon={Headphones} 
          color="yellow" 
        />
        <StatsCard
          title="Total Videos"
          value={stats.totalVideos}
          subtitle="Published videos"
          icon={Video}
          color="cyan"
        />
        <StatsCard 
          title="Total Quotes" 
          value={stats.totalQuotes} 
          subtitle="Published quotes"
          icon={Quote} 
          color="blue" 
        />
        <StatsCard 
          title="Active Users" 
          value={stats.activeUsers} 
          subtitle="Registered users"
          icon={Users} 
          color="red" 
        />
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            <button
              onClick={() => setShowQuickActions(!showQuickActions)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              {showQuickActions ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show
                </>
              )}
            </button>
          </div>
        </div>
        
        {showQuickActions && (
          <div className="card-body">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <button 
                className="btn btn-primary btn-sm flex flex-col items-center gap-2 py-3"
                onClick={() => navigate('/ebooks/new')}
              >
                <BookOpen className="w-5 h-5" />
                <span className="text-xs">Add E-book</span>
              </button>
              
              <button 
                className="btn btn-success btn-sm flex flex-col items-center gap-2 py-3"
                onClick={() => navigate('/audio/new')}
              >
                <Headphones className="w-5 h-5" />
                <span className="text-xs">Add Audio</span>
              </button>

              <button
                className="btn btn-info btn-sm flex flex-col items-center gap-2 py-3"
                onClick={() => navigate('/videos/new')}
              >
                <Video className="w-5 h-5" />
                <span className="text-xs">Add Video</span>
              </button>
              
              <button 
                className="btn btn-warning btn-sm flex flex-col items-center gap-2 py-3"
                onClick={() => navigate('/quotes')}
              >
                <Quote className="w-5 h-5" />
                <span className="text-xs">Add Quote</span>
              </button>
              
              <button 
                className="btn btn-secondary btn-sm flex flex-col items-center gap-2 py-3"
                onClick={() => navigate('/users')}
              >
                <Users className="w-5 h-5" />
                <span className="text-xs">View Users</span>
              </button>
              
              <button 
                className="btn btn-info btn-sm flex flex-col items-center gap-2 py-3"
                onClick={() => navigate('/analytics')}
              >
                <Download className="w-5 h-5" />
                <span className="text-xs">Analytics</span>
              </button>
              
              <button 
                className="btn btn-gray btn-sm flex flex-col items-center gap-2 py-3"
                onClick={() => navigate('/settings')}
              >
                <Settings className="w-5 h-5" />
                <span className="text-xs">Settings</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Latest Updates */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Latest Updates</h3>
          <p className="text-sm text-gray-600">Most recent content additions</p>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Latest Quote */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Quote className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-1">Latest Quote</h4>
                <p className="text-sm text-gray-600 mb-2">
                  {stats.latestQuote ? 
                    `"${stats.latestQuote.content.substring(0, 60)}${stats.latestQuote.content.length > 60 ? '...' : ''}"` : 
                    'No quotes yet'
                  }
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{stats.latestQuote ? new Date(stats.latestQuote.createdAt).toLocaleDateString() : 'N/A'}</span>
                  {stats.latestQuote && (
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      stats.latestQuote.isPublished 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {stats.latestQuote.isPublished ? 'Published' : 'Draft'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Latest E-book */}
            <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
              <div className="p-2 bg-green-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-1">Latest E-book</h4>
                <p className="text-sm text-gray-600 mb-2">
                  {stats.latestEbook ? 
                    stats.latestEbook.title || 'Untitled E-book' : 
                    'No e-books yet'
                  }
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{stats.latestEbook ? new Date(stats.latestEbook.createdAt).toLocaleDateString() : 'N/A'}</span>
                  {stats.latestEbook && (
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      stats.latestEbook.isPublished 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {stats.latestEbook.isPublished ? 'Published' : 'Draft'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Latest Audio */}
            <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Headphones className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-1">Latest Audio</h4>
                <p className="text-sm text-gray-600 mb-2">
                  {stats.latestAudio ? 
                    stats.latestAudio.title || 'Untitled Audio' : 
                    'No audio books yet'
                  }
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{stats.latestAudio ? new Date(stats.latestAudio.createdAt).toLocaleDateString() : 'N/A'}</span>
                  {stats.latestAudio && (
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      stats.latestAudio.isPublished 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {stats.latestAudio.isPublished ? 'Published' : 'Draft'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Latest Video */}
            <div className="flex items-start gap-3 p-4 bg-cyan-50 rounded-lg">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Video className="w-5 h-5 text-cyan-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-1">Latest Video</h4>
                <p className="text-sm text-gray-600 mb-2">
                  {stats.latestVideo ? stats.latestVideo.title || 'Untitled Video' : 'No videos yet'}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{stats.latestVideo ? new Date(stats.latestVideo.createdAt).toLocaleDateString() : 'N/A'}</span>
                  {stats.latestVideo && (
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        stats.latestVideo.isPublished ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {stats.latestVideo.isPublished ? 'Published' : 'Draft'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
